import React, {useCallback, useReducer, useRef, useState} from "react";
import Downshift from "downshift";
import styled from "styled-components";
import Observable from "zen-observable";
import {useHistory} from "react-router";
import {observeDelayMillis} from "../../../../apiObservables";
import {ajax} from "@timgroup/common-javascript-components";
import {log} from "../../../../logging/logEvent";
import Loader from "../../../elements/Loader";
import {borderColour, inputPlaceholderText, primaryTextColour} from "../../../../style/styleConsts";
import {useSingleSubscriptionHolder} from "../../../../hooks/SubscriptionHolder";
import {useDebounced} from "../../../../hooks/Debounced";
import activeIcon from "../../../../../images/icon-magnifying-dark@2x.png";
import inactiveIcon from "../../../../../images/icon-magnifying-light@2x.png";
import {SuggestionItem} from "./SuggestionItem";

export const debounceTimeout = 100;

export const showLoadingTimeout = debounceTimeout + 300;

function makeString(suggestion) {
    if (suggestion === null)
        return "";
    const {type, data} = suggestion;
    switch (type) {
        case "ORGANISATION":
            return data.companyName;
        case "AUTHOR":
            return `${data.firstName} ${data.lastName}`;
        default:
            throw new Error(`Unhandled suggestion type: ${type}`);
    }
}

const Search = ({}) => {
    const history = useHistory();
    const [loadingIcon, setLoadingIcon] = useState(false);
    const searchTerm = useRef("");
    const [version, bumpVersion] = useReducer(a => a + 1, 0);
    const registerSuggestionsLoader = useSingleSubscriptionHolder([]);
    const registerShowIcon = useSingleSubscriptionHolder([]);
    const [suggestions, setSuggestions] = useState([]);
    const [noSuggestions, setNoSuggestions] = useState(false);

    const loadSuggestions = useDebounced(newSearchTerm => {
        const loadingIconSubscription = registerShowIcon(observeDelayMillis(showLoadingTimeout - debounceTimeout).subscribe({
            complete() {
                setLoadingIcon(true);
            }
        }));
        registerSuggestionsLoader(ajax.get(`/ui/api/search?q=${newSearchTerm}`).subscribe(
            ({ data: { suggestions: newSuggestions }}) => {
                setSuggestions(newSuggestions);
                setNoSuggestions(newSuggestions.length === 0);
                loadingIconSubscription.unsubscribe();
                setLoadingIcon(false);
            }
        ));
    }, debounceTimeout);

    const onStateChange = useCallback((changes, stateAndHelpers) => {
        if (changes.hasOwnProperty("inputValue") && changes.type === Downshift.stateChangeTypes.changeInput) {
            const newSearchTerm = changes.inputValue;
            searchTerm.current = newSearchTerm;
            if (newSearchTerm === "") {
                stateAndHelpers.closeMenu();
            }
            else {
                loadSuggestions(newSearchTerm);
            }
        }
        if (changes.hasOwnProperty("isOpen") && !changes.isOpen) {
            setSuggestions([]);
            setNoSuggestions(false);
            setLoadingIcon(false);
            // effectively cancel any outstanding load
            registerSuggestionsLoader(Observable.of().subscribe());
            registerShowIcon(Observable.of().subscribe());
        }
    }, []);

    const onSuggestionSelected = useCallback((selection, state) => {
        if (!selection) return;
        const { type, data } = selection;
        bumpVersion(); // forces a new Downshift instance
        if (type === "AUTHOR") {
            log("SuggestionSelected", { type, searchTerm: searchTerm.current, id: data.userId});
            history.push(`/author/${data.userId}`);
        } else {
            log("SuggestionSelected", { type, searchTerm: searchTerm.current, id: data.orgId.toString()});
            history.push(`/stock/${data.orgId}`);
        }
    }, []);

    function makeKey({type, data}) {
        switch (type) {
            case "ORGANISATION":
                return `ORGANISATION-${data.orgId}`;
            case "AUTHOR":
                return `AUTHOR-${data.userId}`;
            default:
                throw new Error(`Unhandled suggestion type: ${type}`);
        }
    }

    return (
        <Downshift key={`${version}`} onChange={onSuggestionSelected} onStateChange={onStateChange} itemToString={makeString}>
            {({getInputProps, getItemProps, getLabelProps, getMenuProps, isOpen, highlightedIndex, selectedItem}) => (
                <div style={{position: "relative"}}>
                    <label {...getLabelProps()} style={{display: "none"}}>Find stock or contributor</label>
                    <Input {...getInputProps()} placeholder="Find stock or contributor" isOpen={isOpen} />
                    {loadingIcon ? <div style={{zIndex: 1, position: "absolute", right: 8, top: 6}}><Loader /></div> : null }
                    <Container>
                        { isOpen ? <List {...getMenuProps()}>
                            { suggestions.map((item, index) => (
                                <SuggestionItem key={makeKey(item)}
                                                item={item} index={index}
                                                getItemProps={getItemProps}
                                                highlightedIndex={highlightedIndex}/>
                            ))}
                            { noSuggestions ? <div className="t-no-results" style={{ padding: "10px 15px 10px 35px", width: 500, cursor: "default"}}>No results</div> : null }
                        </List> : null }
                    </Container>
                </div>
            )}
        </Downshift>
    );
};

const Container = styled.div`
    position: absolute;
    z-index: 10;
    background-color: white;
    border-top: 1px solid ${borderColour};
    border-radius: 0 0 4px 4px;
    box-shadow: 0 2px 3px 1px rgba(0,0,0,0.15);
    overflow: hidden;
`;

const Input = styled.input`
    width: 403px;
    padding: 8px 15px 8px 32px;
    border: none;
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
    border-bottom-right-radius: ${props => props.isOpen ? "0" : "4px"};
    border-bottom-left-radius: ${props => props.isOpen ? "0" : "4px"};
    background-color: white;
    color: white;
    background-image: url(${inactiveIcon});
    background-size: 18px 19px;
    background-repeat: no-repeat;
    background-position: 9px center;

    ::placeholder {
        color: ${inputPlaceholderText};
    }
    
    :focus {
        outline: none;
        color: ${primaryTextColour};
        background-color: white;
        background-image: url(${activeIcon});
    }
    
    :focus::placeholder {
        color: ${inputPlaceholderText};
    }
`;

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style-type: none;
`;

export default Search;
